/* eslint-disable valid-typeof */
import h from './common';
import url from './url';
import moment from 'moment';
import { __ } from './../translations';
import { appConfig } from '../config/app.config';

interface Meta {
    title?: {
        en?: string;
        ar?: string;
    };
    carouselType?: string;
    is_rented?: boolean;
}

interface Params {
    filter_params?: string[];
}

interface Carousel {
    filters?: any[];
    sortingOptions?: any[];
}

/**
 * Converts a given string to its Base64 encoded representation.
 *
 * @param str - The string to be encoded.
 * @returns The Base64 encoded string.
 */
export const toBase64 = (str) => {
    return btoa(
        Array.from(new TextEncoder().encode(str))
            .map((byte) => String.fromCharCode(byte))
            .join('')
    );
};

/**
 * Decodes a Base64-encoded string into a UTF-8 string.
 *
 * @param base64 - The Base64-encoded string to decode.
 * @returns The decoded UTF-8 string.
 */
export const fromBase64 = (base64) => {
    return new TextDecoder().decode(
        new Uint8Array(
            atob(base64)
                .split('')
                .map((char) => char.charCodeAt(0))
        )
    );
};

const formatter = {
    getComponentTitle: (meta: Meta): string => {
        const lang = h.getLang() || 'en';
        if (typeof meta.title !== 'undefined') {
            if (lang === 'en' && typeof meta.title.en !== undefined) {
                return meta.title.en;
            } else if (typeof meta.title.ar !== undefined) {
                return meta.title.ar;
            } else {
                return '';
            }
        }
        return '';
    },
    getCarouselType: (meta: Meta): string | undefined => {
        return meta.carouselType;
    },
    getComponentContents: (content: { en?: string; ar?: string }): string => {
        const lang = h.getLang();
        let htmlContent = '';

        if (content) {
            if (lang === 'en' && typeof content.en !== undefined) {
                htmlContent = content.en || '';
            } else if (typeof content.ar !== undefined) {
                htmlContent = content.ar || '';
            }
        }

        //  FIXED: Sanitize HTML to prevent nested html/body tags that cause hydration errors
        if (htmlContent) {
            // Remove html, head, body tags that shouldn't be nested
            htmlContent = htmlContent
                .replace(/<\/?html[^>]*>/gi, '')
                .replace(/<\/?head[^>]*>/gi, '')
                .replace(/<\/?body[^>]*>/gi, '')
                .replace(/<\/?!DOCTYPE[^>]*>/gi, '')
                .trim();
        }

        return htmlContent;
    },

    getGenreLink: (item: any): string | boolean => {
        const LinkParams: { [key: string]: string | boolean } = item.filters
            ? {
                  filters: btoa(item.filters[0]),
              }
            : {};

        if (item.dataSources) LinkParams.data_stream = btoa(item.dataSources[0]);
        LinkParams.filters = item?.filters?.length ? btoa(JSON.stringify(item?.filters)) : btoa(JSON.stringify([]));
        LinkParams.sortingOptions = item?.sortingOptions?.length
            ? btoa(JSON.stringify(item?.sortingOptions))
            : btoa(JSON.stringify([]));
        LinkParams.title = btoa(item.title_resource_key);
        return url.route('vod-list', LinkParams);
    },
    /**
     * Helper to check if seeAll like or not and return the url route
     * @param meta
     * @param params
     * @param dataStream
     * @param carousel
     * @param formattedTitle
     * @returns {string|boolean}
     */
    getComponentSeeAllLink: (
        meta: Meta,
        params: Params,
        dataStream: string,
        carousel?: Carousel,
        formattedTitle?: string
    ): string | boolean => {
        if (typeof meta && params && carousel?.filters) {
            const LinkParams: { [key: string]: string | boolean } = params.filter_params
                ? {
                      filter_params: btoa(params.filter_params[0]),
                  }
                : {};

            if (dataStream) LinkParams.data_stream = btoa(dataStream);
            LinkParams.filters = carousel?.filters?.length
                ? btoa(JSON.stringify(carousel?.filters))
                : btoa(JSON.stringify([]));
            LinkParams.sortingOptions = carousel?.sortingOptions?.length
                ? btoa(JSON.stringify(carousel?.sortingOptions))
                : btoa(JSON.stringify([]));
            if (meta?.is_rented) LinkParams.rented = true;
            LinkParams.title = formattedTitle ? toBase64(formattedTitle) : '';

            return url.route('vod-list', LinkParams);
        }
        return false;
    },
    /**
     * Function to convert timeStamp to date with translations
     * @param timeStamp
     * @returns {string}
     */
    formatDate: (timeStamp: string): string => {
        const rDate = new Date(parseInt(timeStamp, 10));
        if (h.getLang() === 'ar') {
            return rDate.getDate() + ' ' + __(`month_${rDate.getMonth() + 1}`) + ' ' + rDate.getFullYear();
        }
        return __(`month_${rDate.getMonth() + 1}`) + ' ' + rDate.getDate() + ' ' + rDate.getFullYear();
    },
    /**
     * Function to convert timeStamp to monthname and day with translations
     * @param timeStamp
     * @returns {string}
     */
    toLocaleDateAndMonthName: (timeStamp: string): string => {
        const rDate = new Date(parseInt(timeStamp, 10));
        if (h.getLang() === 'ar') {
            return rDate.getDate() + ' ' + __(`month_${rDate.getMonth() + 1}`);
        }
        return __(`month_${rDate.getMonth() + 1}`) + ' ' + rDate.getDate();
    },
    formatRewindDate(d: string, showFullFormat: boolean, dayNameOrFormat = ''): string {
        //format in fullformat TODAY JUN 01 | 03:16PM
        const date = moment(new Date(d));
        const currentLang = h.getLang();
        const itemDay = +date.format('DD');
        //  FIXED: Safely handle locale setting without requiring dynamic imports
        try {
            if (currentLang === 'en') {
                date.locale('en');
            } else {
                date.locale('ar-sa');
            }
        } catch (error) {
            // Fallback to English if Arabic locale is not available
            date.locale('en');
        }
        // const currentDay = new Date().getDay();
        const currentDay = +moment().format('DD');
        const tomorrow = +moment().add(1, 'days').format('DD');
        const yesterday = +moment().add(-1, 'days').format('DD');
        let dayName = '';
        if (currentDay === itemDay) {
            dayName = __('date_today');
        } else if (tomorrow === itemDay) {
            dayName = __('date_tomorrow');
        } else if (yesterday === itemDay) {
            dayName = __('date_yesterday');
        }
        if (dayNameOrFormat) {
            return dayName ? dayName : `${date.format('DD/MM')}`.toUpperCase();
        } else if (showFullFormat) {
            return `${dayName} ${date.format('MMM DD | hh:mm a')}`.toUpperCase();
        } else {
            return date.format('hh:mm a').toUpperCase();
        }
    },
    isArDownLetters: (val: string): boolean => new RegExp(`^(?:[${appConfig.arabicDownLetters}]+)$`, 'i').test(val),
};

export default formatter;
